import Alert from '@material-ui/lab/Alert'
import { each, get, isEmpty, isEqual, pick } from 'lodash'
import { useCallback, useState } from 'react'
import {
  ArrayInput,
  BooleanInput,
  EditController,
  EditView,
  FormTab,
  Loading,
  maxLength,
  minLength,
  number,
  NumberInput,
  required,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
} from 'react-admin'
import { useFormState } from 'react-final-form'

import { captureExceptionIfProduction, localizeDateTimeAndStripOffset } from 'helpers/utils'
import { modifyLoadStops, partialUpdateJob, partialUpdateLoad } from '../../api/jobs'
import {
  JobStatus,
  LoadingTypeChoices,
  STOP_TYPE_CHOICES,
  StopTypeDetailChoices,
  SUPPORTED_EQUIPMENT_CHOICES,
  TIMEZONE_CHOICES,
} from '../../helpers/constants'
import { getCountryFromCode } from '../../helpers/countries'
import { getStateChoices } from '../../helpers/states'
import { validateInteger, validateLoadStops, validateTwoDecimals } from '../../helpers/validators'
import { hasPermission, Permissions } from '../../Permissions'
import { BrokerStatus } from '../Brokers/BrokerStatusTransitions'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from '../Common/Brokers'
import { StopDateTimeInputWindow, StopSequenceNumberWrapper } from '../Fields'

const SaveButtonAltered = (props: any) => {
  const { record, basePath, jobFields, loadFields, loadId, initialValues } = props
  const notify = useNotify()
  const redirectTo = useRedirect()
  const formState = useFormState()
  const [loading, setLoading] = useState(false)

  const handleClick = useCallback(
    async (values, redirect) => {
      try {
        setLoading(true)

        const loadValues = pick(values, loadFields)
        const initialLoadValues = pick(initialValues, loadFields)
        if (!isEqual(loadValues, initialLoadValues)) {
          // no need to update the load if it hasn't changed
          await partialUpdateLoad(loadId, loadValues)
        }

        const stops = get(values, 'stops')
        const initialStops = get(initialValues, 'stops')
        if (!isEqual(stops, initialStops)) {
          // no need to update the stops if they haven't changed
          await modifyLoadStops(loadId, stops)
        }

        const jobValues = pick(values, jobFields)
        const response = await partialUpdateJob(record.id, jobValues)

        notify('Job Updated Successfully', { type: 'success' })
        redirectTo(redirect, basePath, record.id, response)
        setLoading(false)
      } catch (error: any) {
        setLoading(false)
        const data = get(error, 'response.data')
        const stringified = data ? JSON.stringify(data) : ''
        notify(`${error.message}: ${stringified}`, {
          type: 'error',
        })
        captureExceptionIfProduction(error)
      }
    },
    [notify, basePath, redirectTo, record, jobFields, loadFields, loadId, initialValues]
  )

  const loadStopErrors = formState?.errors?.load_stops
  if (loadStopErrors) {
    return <Alert severity="error">{loadStopErrors}</Alert>
  }
  if (!isEmpty(formState.errors)) {
    return <Alert severity="error">Please fix the errors above: {JSON.stringify(formState.errors)}</Alert>
  }

  return <SaveButton {...props} disabled={loading} onSave={handleClick} />
}

const JobEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButtonAltered
      label="Save"
      redirect="show"
      submitOnEnter={false}
      variant="text"
      loadId={props.loadId}
      jobFields={props.jobFields}
      loadFields={props.loadFields}
      initialValues={props.initialValues}
    />
  </Toolbar>
)

const JobEdit = (props: any) => (
  <EditController {...props}>
    {(controllerProps) => {
      const record = controllerProps.record
      const jobValues = {
        payment_in_dispute: record?.payment_in_dispute,
        hazmat_detected: record?.hazmat_detected,
      }

      const load = record?.load
      const loadValues = {
        broker_id: load?.broker?.id,
        company_name: load?.company_name,
        contact_name: load?.contact_name,
        contact_email: load?.contact_email,
        contact_phone: load?.contact_phone,
        source_human_id: load?.source_human_id,
        equipment: load?.equipment,
        trip_rate: load?.trip_rate,
        is_load_out_trailer: load?.is_load_out_trailer,
        truck_full_load: load?.truck_full_load,
        truck_length_ft: load?.truck_length_ft,
        truck_weight_lb: load?.truck_weight_lb,
      }

      const stops = load?.stops
      each(stops, (stop) => {
        stop.date_start = localizeDateTimeAndStripOffset(stop.date_start, stop.location_timezone)
        stop.date_end = localizeDateTimeAndStripOffset(stop.date_end, stop.location_timezone)
      })

      const initialValues = {
        ...jobValues,
        ...loadValues,
        stops,
      }

      const country: any = getCountryFromCode('US')
      const stateChoices = getStateChoices(country.states(true))

      return controllerProps.loading ? (
        <Loading />
      ) : (
        <EditView {...props} {...controllerProps}>
          <TabbedForm
            initialValues={initialValues}
            toolbar={
              <JobEditToolbar
                loadId={load?.id}
                jobFields={Object.keys(jobValues)}
                loadFields={Object.keys(loadValues)}
                initialValues={initialValues}
              />
            }
            validate={(values: any) => validateLoadStops({ stops: values.stops })}
          >
            <FormTab label="Job Disputes">
              <BooleanInput source="payment_in_dispute" label="Payment In Dispute" />
              <BooleanInput source="hazmat_detected" label="Hazmat detected" />
            </FormTab>

            {(record?.status !== JobStatus.Past ||
              hasPermission(props?.permissions, Permissions.ChangeBrokerOnPastJob)) && (
              <FormTab label="Load Info">
                <BrokerReferenceInput
                  source="broker_id"
                  label="Broker"
                  reference="brokers"
                  filter={{ status: BrokerStatus.Approved.id }}
                  filterToQuery={(searchText: any) => ({ search: searchText })}
                  containerClassName={BrokerReferenceInputClassName.load}
                  showQuickCreate={false}
                  validate={required()}
                />
                {record?.status !== JobStatus.Past && (
                  <>
                    <TextInput source="company_name" label="Shipper name" />
                    <TextInput source="contact_name" label="Load contact name" />
                    <TextInput source="contact_email" label="Load contact email" />
                    <TextInput source="contact_phone" label="Load contact phone" />
                  </>
                )}
                <TextInput source="source_human_id" label="Broker load id" />
              </FormTab>
            )}

            {record?.status !== JobStatus.Past && (
              <FormTab label="Trip Details">
                <SelectArrayInput
                  source="equipment"
                  validate={required()}
                  choices={SUPPORTED_EQUIPMENT_CHOICES}
                />
                <NumberInput source="trip_rate" validate={[number(), validateTwoDecimals]} />
                {record?.status === JobStatus.Pending && (
                  <BooleanInput source="is_load_out_trailer" label="Load Out Trailer" />
                )}
                <BooleanInput source="truck_full_load" label="Full Truck Load" />
                <NumberInput source="truck_length_ft" validate={validateInteger} />
                <NumberInput source="truck_weight_lb" validate={validateInteger} />
              </FormTab>
            )}

            {record?.status !== JobStatus.Past && (
              <FormTab label="Load Stops">
                <ArrayInput source="stops">
                  <SimpleFormIterator>
                    <StopSequenceNumberWrapper label="Sequence number" validate={required()} />
                    <SelectInput
                      source="type"
                      choices={STOP_TYPE_CHOICES}
                      label="Stop type"
                      validate={required()}
                    />
                    <SelectInput
                      source="type_detail"
                      label="Type detail"
                      choices={StopTypeDetailChoices}
                      validate={required()}
                    />
                    <SelectInput source="loading_type" label="Loading type" choices={LoadingTypeChoices} />
                    <StopDateTimeInputWindow />
                    <SelectInput
                      source="location_timezone"
                      label="Timezone"
                      choices={TIMEZONE_CHOICES}
                      validate={required()}
                    />
                    <TextInput source="location_name" label="Facility name" />
                    <TextInput source="location_address1" label="Location address 1" />
                    <TextInput source="location_address2" label="Location address 2" />
                    <TextInput source="location_city" label="City" validate={required()} />
                    <SelectInput
                      source="location_state"
                      label="State"
                      choices={stateChoices}
                      validate={[required(), maxLength(2), minLength(2)]}
                    />
                    <TextInput source="location_zip" label="Zip" />
                    <TextInput source="notes" multiline fullWidth label="Notes" />
                    <TextInput source="reference_code" label="Reference code" validate={maxLength(250)} />
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
            )}
          </TabbedForm>
        </EditView>
      )
    }}
  </EditController>
)

export default JobEdit
